import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiControlledNumericInput } from "@miplatform/components";
import { BUTTON_COLORS } from "@miplatform/constants";
import { ICategoryDropsResponse } from "@miplatform/types";
import { isRecentlyAdded } from "@miplatform/utils";
import { mdiTrashCanOutline } from "@mdi/js";
import { AccordionDetails, Box, Typography } from "@mui/material";
import { IPriceLevelPayload } from "../PricingMatrix.types";
import {
  DROP_NAME_COL_WIDTH,
  PRICE_LVL_COL_WIDTH,
} from "../constants/constants";

export function CategoryDropRows({
  readOnly = false,
  form,
  transform,
  categoryId,
  categoryDrop,
  categoryDrops = [],
  priceLevels,
  activePriceLevelIndex,
  handleDeleteCategoryDrop,
}: CategoryDropRowsProps) {
  const { control, setValue } = form ?? {};
  const { classes, cx } = useStyles({ transform });

  const getDropInfo = (
    priceLevel: IPriceLevelPayload,
    drop: { dropTypeId: string; categoryId: string }
  ) => {
    const dropPrices = priceLevel.dropPrices.find(
      (dp) =>
        dp.dropTypeId === drop.dropTypeId && dp.categoryId === drop.categoryId
    );
    const dropIndex = categoryDrops.findIndex(
      (cd) =>
        cd.dropTypeId === drop.dropTypeId && cd.categoryId === drop.categoryId
    );
    return {
      price: dropPrices?.price,
      modifiedOn: dropPrices?.modifiedOn,
      dropIndex,
    };
  };

  return (
    <AccordionDetails className={classes.accordionDetails}>
      {categoryDrop.map((drop) => {
        return (
          <Box key={drop.id} className={classes.dropBox}>
            <Box className={classes.dropNameCol}>{drop.dropType}</Box>
            <Box className={classes.scrollWrapper}>
              <Box className={classes.scrollContent}>
                {priceLevels.map((level, plIdx) => {
                  const { price, modifiedOn, dropIndex } = getDropInfo(
                    level,
                    drop
                  );
                  const isRecentlyModified = isRecentlyAdded(
                    modifiedOn,
                    5,
                    "seconds"
                  );
                  const isActive = activePriceLevelIndex === plIdx;

                  return (
                    <Box
                      key={level.id}
                      className={cx(
                        classes.priceLevelCol,
                        isActive
                          ? classes.priceLevelColActive
                          : isRecentlyModified && classes.recentlyModified
                      )}
                    >
                      {isActive && control ? (
                        <MiControlledNumericInput
                          formControl={control}
                          value={price}
                          prefix="$"
                          readOnly
                          className={classes.priceInputField}
                          decimalScale={2}
                          onValueChange={(values: { floatValue: number }) => {
                            setValue?.(
                              `dropPrices.${dropIndex}.price`,
                              values.floatValue || 0,
                              { shouldDirty: true }
                            );
                            setValue?.(
                              `dropPrices.${dropIndex}.dirty`, // for client only, to only send changed drop price to backend
                              true
                            );
                          }}
                        />
                      ) : (
                        <Box className={classes.priceBox}>
                          <Typography className={classes.priceText}>
                            ${price}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
            {!readOnly && (
              <MiButton
                disabled={activePriceLevelIndex !== null}
                startIcon={{ path: mdiTrashCanOutline }}
                color={BUTTON_COLORS.RED}
                size="small"
                onClick={() => {
                  // TODO: JP add modal later in actions story. for now, delete on click.
                  handleDeleteCategoryDrop(categoryId, drop);
                }}
              />
            )}
          </Box>
        );
      })}
    </AccordionDetails>
  );
}

const useStyles = makeStyles<StyleProps>()((theme, { transform }) => ({
  accordionDetails: {
    backgroundColor: theme.palette.grey.light,
  },
  dropBox: {
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    marginTop: theme.spacing(1),
    height: 39,
  },
  priceInputField: {
    "& .MuiInputBase-input.MuiOutlinedInput-input": {
      padding: 0,
      fontSize: theme.fontSize.medium,
    },
  },
  priceBox: {
    height: 23,
    display: "flex",
    alignItems: "center",
  },
  priceText: {
    fontSize: theme.fontSize.medium,
  },
  // shared styles with PriceLevelHeaderRow
  dropNameCol: {
    width: DROP_NAME_COL_WIDTH,
    minWidth: DROP_NAME_COL_WIDTH,
    fontWeight: theme.fontWeight.semiBold,
    color: theme.palette.primary.main,
    paddingLeft: theme.spacing(2),
  },
  scrollWrapper: {
    flex: 1,
    overflow: "hidden",
    minWidth: 0,
  },
  scrollContent: {
    display: "flex",
    transition: "transform 0.3s ease",
    transform,
  },
  priceLevelCol: {
    width: PRICE_LVL_COL_WIDTH,
    minWidth: PRICE_LVL_COL_WIDTH,
    flexShrink: 0,
    backgroundColor: theme.palette.grey.mediumLight,
    display: "flex",
    alignItems: "center",
    padding: 8,
    borderRight: `1px solid ${theme.palette.divider}`,
    justifyContent: "space-between",
  },
  priceLevelColActive: {
    backgroundColor: `${theme.palette.primary.light}`,
  },
  recentlyModified: {
    backgroundColor: theme.palette.orange.light,
  },
}));

interface CategoryDropRowsProps {
  readOnly?: boolean;
  form?: UseFormReturn<IPriceLevelPayload>;
  transform: string;
  categoryId: string;
  categoryDrop: ICategoryDropsResponse[];
  categoryDrops?: ICategoryDropsResponse[];
  priceLevels: IPriceLevelPayload[];
  activePriceLevelIndex: number | null;
  handleDeleteCategoryDrop: (
    categoryId: string,
    drop: Partial<ICategoryDropsResponse>
  ) => void;
}

interface StyleProps {
  transform: string;
}
